import { HelpTopic } from 'shared';

/** The languages with CrownBase manuals (docs/help/<language>), served next to the app. */
const HELP_LANGUAGES = ['nl', 'fr'];

/** Where the manuals in the user's language are; Dutch for any other language. */
export function helpBasePath(language: string | null | undefined): string {
  return `/help/${HELP_LANGUAGES.includes(language ?? '') ? language : 'nl'}`;
}

/** The manuals, with their Dutch titles; the help shows the title of the manual in the user's language once loaded. */
export const HELP_TOPICS: HelpTopic[] = [
  { article: 'werken-met-crownbase.md', title: 'Werken met CrownBase – de basis' },
  { article: 'zoeken-en-gebruikersinstellingen.md', title: 'Zoeken en gebruikersinstellingen' },
  { article: 'dashboard.md', title: 'Dashboard' },
  { article: 'verkoop.md', title: 'Verkoop' },
  { article: 'kostenregels.md', title: 'Kostenregels' },
  { article: 'aankoop.md', title: 'Aankoop' },
  { article: 'magazijn.md', title: 'Magazijn' },
  { article: 'klanten.md', title: 'Klanten' },
  { article: 'leveranciers.md', title: 'Leveranciers' },
  { article: 'artikelen.md', title: 'Artikelen' },
  { article: 'klachten.md', title: 'Klachten' },
  { article: 'communicatie.md', title: 'Communicatie' },
  { article: 'intranet.md', title: 'Intranet' },
  { article: 'bestanden.md', title: 'Bestanden' },
  { article: 'medewerkers.md', title: 'Medewerkers' },
  { article: 'logboeken.md', title: 'Logboeken' },
  { article: 'instellingen.md', title: 'Instellingen' },
];

/** The first that matches the url (without query) gives the manual, and the section, of a screen. */
const HELP_ARTICLES: [RegExp, string][] = [
  [/^\/dashboard/, 'dashboard.md'],
  [/^\/users\/settings/, 'zoeken-en-gebruikersinstellingen.md#je-gebruikersinstellingen'],

  [/^\/sales\/(orders\/)?client_quotes/, 'verkoop.md#offertes'],
  [/^\/sales\/orders\/group_order/, 'verkoop.md#groepsorders'],
  [/^\/sales\/orders/, 'verkoop.md#orders'],
  [/^\/sales\/client_documents/, 'verkoop.md#order-uploads'],
  [/^\/sales\/client_invoices/, 'verkoop.md#verkoopfacturen'],
  [/^\/sales\/client_credit_notes/, 'verkoop.md#verkoopcreditnotas'],
  [/^\/sales\/client_returns/, 'verkoop.md#retouren'],
  [/^\/sales\/stock_sales_reports/, 'verkoop.md#stock--sales-reports'],
  [/^\/sales/, 'verkoop.md'],

  [/^\/purchase\/supplier_orders/, 'aankoop.md#bestellingen-leveranciersbestellingen'],
  [/^\/purchase\/supplier_documents/, 'aankoop.md#aankoop-uploads'],
  [/^\/purchase\/supplier_invoices/, 'aankoop.md#aankoopfacturen'],
  [/^\/purchase\/supplier_credit_notes/, 'aankoop.md#aankoopcreditnotas'],
  [/^\/purchase\/supplier_complaints/, 'aankoop.md#aankoopklachten'],
  [/^\/purchase/, 'aankoop.md'],

  [/^\/warehouse\/stock_reservations/, 'magazijn.md#reserveringen'],
  [/^\/warehouse\/pick_orders/, 'magazijn.md#pakbonnen'],
  [/^\/warehouse\/deliveries/, 'magazijn.md#leveringen'],
  [/^\/warehouse\/receipts/, 'magazijn.md#ontvangsten'],
  [/^\/warehouse\/stock_(movements|corrections)/, 'magazijn.md#stock-bewegingen-en-stock-correcties'],
  [/^\/warehouse\/stock_locations/, 'magazijn.md#stock-locaties'],
  [/^\/warehouse\/stocks/, 'magazijn.md#stock-artikelen'],
  [/^\/warehouse\/stock_items/, 'magazijn.md#stock-items'],
  [/^\/warehouse\/stock_suggestions/, 'magazijn.md#stock-suggesties'],
  [/^\/warehouse\/warehouse_shipments/, 'magazijn.md#magazijnleveringen'],
  [/^\/warehouse/, 'magazijn.md'],

  [/^\/clients\/[^/]+\/general/, 'klanten.md#gegevens-wijzigen'],
  [/^\/clients\/[^/]+\/contacts/, 'klanten.md#afdelingen'],
  [/^\/clients\/[^/]+\/employees/, 'klanten.md#werknemers'],
  [/^\/clients\/[^/]+\/showrooms/, 'klanten.md#toonzalen'],
  [/^\/clients\/[^/]+\/deliveryAddresses/, 'klanten.md#leveradressen'],
  [/^\/clients\/[^/]+\/deliveryInfo/, 'klanten.md#leverancierskoppelingen'],
  [/^\/clients\/[^/]+\/client_has_supplier_brands/, 'klanten.md#aankoopvoorwaarden'],
  [/^\/clients\/[^/]+\/visit-reports/, 'klanten.md#bezoekrapporten'],
  [/^\/clients\/[^/]+\/agreements/, 'klanten.md#overeenkomsten'],
  [/^\/clients\/[^/]+\/client_events/, 'klanten.md#gebeurtenissen'],
  [/^\/clients\/[^/]+\/offline_media/, 'klanten.md#offline-media'],
  [/^\/clients\/[^/]+\/online-media/, 'klanten.md#online-media'],
  [/^\/clients\/[^/]+\/articles/, 'klanten.md#artikelen'],
  [/^\/clients\/[^/]+\/brand_representatives/, 'klanten.md#vertegenwoordigers'],
  [/^\/clients\/[^/]+\/action_item_submissions/, 'klanten.md#actiepunten'],
  [/^\/clients\/[^/]+\/integrations/, 'klanten.md#integraties'],
  [/^\/clients\/[^/]+\/billing/, 'klanten.md#facturatie'],
  [/^\/clients\/[^/]+\/(insurances|client_logo_medias)/, 'klanten.md#assets'],
  [/^\/clients\/[^/]+\/mailing_lists/, 'klanten.md#mailinglijsten'],
  [/^\/clients/, 'klanten.md'],

  [/^\/suppliers\/[^/]+\/(general|details)/, 'leveranciers.md#gegevens-wijzigen'],
  [/^\/suppliers\/[^/]+\/contacts/, 'leveranciers.md#afdelingen'],
  [/^\/suppliers\/[^/]+\/brands/, 'leveranciers.md#merken'],
  [/^\/suppliers\/[^/]+\/supplier_employees/, 'leveranciers.md#werknemers'],
  [/^\/suppliers\/[^/]+\/delivery-days/, 'leveranciers.md#leverdagen'],
  [/^\/suppliers\/[^/]+\/transport-fees/, 'leveranciers.md#transportkosten'],
  [/^\/suppliers\/[^/]+\/conditions/, 'leveranciers.md#aankoopvoorwaarden'],
  [/^\/suppliers\/[^/]+\/price-lists/, 'leveranciers.md#prijslijsten'],
  [/^\/suppliers\/[^/]+\/order_methods/, 'leveranciers.md#bestelwijzen'],
  [/^\/suppliers\/[^/]+\/client_agreements/, 'leveranciers.md#overeenkomsten'],
  [/^\/suppliers\/[^/]+\/action_items/, 'leveranciers.md#actiepunten'],
  [/^\/suppliers\/[^/]+\/integrations/, 'leveranciers.md#integraties'],
  [/^\/suppliers\/[^/]+\/invoicing/, 'leveranciers.md#facturatie'],
  [/^\/suppliers/, 'leveranciers.md'],

  [/^\/supplier_articles/, 'artikelen.md'],
  [/^\/complaints/, 'klachten.md'],
  [/^\/communication\/email_lists/, 'communicatie.md#mailinglijsten'],
  [/^\/communication\/newsletters/, 'communicatie.md#nieuwsbrieven'],
  [/^\/communication\/price_changes/, 'communicatie.md#prijswijzigingen'],
  [/^\/communication\/announcements/, 'communicatie.md#aankondigingen'],
  [/^\/communication/, 'communicatie.md'],
  [/^\/document_library_folders/, 'intranet.md'],
  [/^\/user_medias/, 'bestanden.md'],

  [/^\/royal_crown_employees\/overview/, 'medewerkers.md#het-overzicht'],
  [/^\/royal_crown_employees\/my_details/, 'medewerkers.md#mijn-gegevens'],
  [/^\/royal_crown_employees\/my_leave/, 'medewerkers.md#mijn-verlof'],
  [/^\/royal_crown_employees\/[^/]+\/settings/, 'medewerkers.md#zichtbare-leveranciers'],
  [/^\/royal_crown_employees\/[^/]+/, 'medewerkers.md#een-medewerker-openen'],
  [/^\/royal_crown_employees/, 'medewerkers.md'],
  [/^\/logs/, 'logboeken.md'],

  [/^\/settings\/brands/, 'instellingen.md#merken'],
  [/^\/settings\/main-brands/, 'instellingen.md#hoofdmerken'],
  [/^\/settings\/warehouses/, 'instellingen.md#magazijnen'],
  [/^\/settings\/royal-crown-delivery-addresses/, 'instellingen.md#royal-crown-leveradressen'],
  [/^\/settings\/(client_vat_rates|ledger_accounts|tax-types)/, 'instellingen.md#financiële-lijsten'],
  [/^\/settings\/document-fee-types/, 'instellingen.md#document-kosten'],
  [/^\/settings\/flyer-formats/, 'instellingen.md#folder-formaten'],
  [/^\/settings\/postal-codes/, 'instellingen.md#postcodes'],
  [/^\/settings\/stock_correction_types/, 'instellingen.md#stock-correctie-types'],
  [/^\/settings\/royal_crown_sender_emails/, 'instellingen.md#royal-crown-emailadressen'],
  [/^\/settings/, 'instellingen.md'],
];

/** The manual of the screen at `url`, or the basics when there is none. */
export function helpArticle(url: string): string {
  const path = url.split(/[?#]/)[0];
  return HELP_ARTICLES.find(([pattern]) => pattern.test(path))?.[1] ?? 'werken-met-crownbase.md';
}
